/**
 * Currency formatting composable for Vue components.
 * Provides locale-aware currency formatting with support for dynamic currencies.
 *
 * Usage:
 *   const { formatPrice, formatPriceCents } = useCurrency()
 *   formatPrice(product.price)                    // Uses product's currency
 *   formatPriceCents(1000, 'EUR')                // 1000 cents with EUR currency
 *   formatPriceCents(invoice.amount_in_cents, invoice.currency)
 */

import { useI18n } from "vue-i18n";

// Currency display configuration per locale
// Norwegian locale uses symbol (kr), English uses ISO code (NOK)
const CURRENCY_CONFIG: Record<
  string,
  Record<string, { display: string; symbol: string }>
> = {
  nb: {
    NOK: { display: "narrowSymbol", symbol: "kr" },
    SEK: { display: "narrowSymbol", symbol: "kr" },
    EUR: { display: "narrowSymbol", symbol: "€" },
    USD: { display: "narrowSymbol", symbol: "$" },
    GBP: { display: "narrowSymbol", symbol: "£" },
  },
  en: {
    NOK: { display: "code", symbol: "NOK" },
    SEK: { display: "code", symbol: "SEK" },
    EUR: { display: "code", symbol: "EUR" },
    USD: { display: "code", symbol: "USD" },
    GBP: { display: "code", symbol: "GBP" },
  },
};

// Default currency from account settings, provided via gon
declare const gon: {
  locale: string;
  default_currency?: string;
};

export function useCurrency() {
  const { locale } = useI18n();

  /**
   * Get the current locale, normalized to match our config keys.
   */
  const getCurrentLocale = (): string => {
    const loc = locale.value || gon.locale || "en";
    return loc === "no" ? "nb" : loc;
  };

  /**
   * Get the default currency from account settings or system default.
   */
  const getDefaultCurrency = (): string => {
    return gon.default_currency || "NOK";
  };

  /**
   * Get the currency display mode based on locale.
   * Norwegian: uses symbols (kr)
   * Other locales: uses ISO codes (NOK)
   */
  const getCurrencyDisplay = (
    currency: string,
    currentLocale: string
  ): "code" | "symbol" | "narrowSymbol" | "name" => {
    const localeConfig =
      CURRENCY_CONFIG[currentLocale] || CURRENCY_CONFIG["en"];
    const currencyConfig = localeConfig[currency.toUpperCase()];
    return (currencyConfig?.display || "code") as
      | "code"
      | "symbol"
      | "narrowSymbol"
      | "name";
  };

  /**
   * Format an amount with the specified currency.
   *
   * @param amount - The amount to format (can be Money-like object or number)
   * @param currency - 3-letter currency code (e.g., 'NOK', 'EUR')
   * @param options - Additional Intl.NumberFormat options
   * @returns Formatted currency string
   *
   * @example
   * formatPrice(100, 'NOK')     // nb: "kr 100", en: "NOK 100"
   * formatPrice(99.99, 'EUR')   // nb: "€99,99", en: "EUR 99.99"
   */
  const formatPrice = (
    amount: number | { amount?: number; cents?: number } | null | undefined,
    currency?: string,
    options?: Partial<Intl.NumberFormatOptions>
  ): string => {
    if (amount == null) return "-";

    let numericAmount: number;

    // Handle Money-like objects
    if (typeof amount === "object") {
      numericAmount = amount.amount ?? (amount.cents ?? 0) / 100;
    } else {
      numericAmount = amount;
    }

    const currencyCode = (currency || getDefaultCurrency()).toUpperCase();
    const currentLocale = getCurrentLocale();

    // Map locale to browser locale code
    const browserLocale = currentLocale === "nb" ? "nb-NO" : currentLocale;

    const formatOptions: Intl.NumberFormatOptions = {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits:
        numericAmount === Math.floor(numericAmount) ? 0 : 2,
      minimumFractionDigits:
        numericAmount === Math.floor(numericAmount) ? 0 : 2,
      ...options,
    };

    // Add currencyDisplay separately due to TypeScript limitations
    (formatOptions as Record<string, unknown>).currencyDisplay =
      getCurrencyDisplay(currencyCode, currentLocale);

    try {
      return new Intl.NumberFormat(browserLocale, formatOptions).format(
        numericAmount
      );
    } catch {
      // Fallback for unsupported currencies
      return `${currencyCode} ${numericAmount.toFixed(2)}`;
    }
  };

  /**
   * Format an amount in cents with the specified currency.
   *
   * @param cents - Amount in cents (integer)
   * @param currency - 3-letter currency code
   * @param options - Additional Intl.NumberFormat options
   * @returns Formatted currency string
   *
   * @example
   * formatPriceCents(10000, 'NOK')  // nb: "kr 100", en: "NOK 100"
   */
  const formatPriceCents = (
    cents: number | null | undefined,
    currency?: string,
    options?: Partial<Intl.NumberFormatOptions>
  ): string => {
    if (cents == null) return "-";
    return formatPrice(cents / 100, currency, options);
  };

  return {
    formatPrice,
    formatPriceCents,
    getDefaultCurrency,
    getCurrentLocale,
  };
}
